"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useMemo, useState } from "react";

import { WayflyerLogo } from "@/components/nav/main-nav";

import type { Milestone } from "./plan";
import {
  buildIcs,
  buildPlan,
  fmt,
  fmtLong,
  nextBlackFriday,
  today,
  toDateValue,
  weeksBetween,
} from "./plan";
import { DEFAULT_LEAD_DAYS, LEAD_OPTIONS, STRINGS } from "./strings";

export function BackwardsPlanner() {
  const [targetDate, setTargetDate] = useState<string>("");
  const [leadDays, setLeadDays] = useState<number>(DEFAULT_LEAD_DAYS);
  // Date-dependent state is resolved on the client only, so the server render
  // (which has no stable "today") never mismatches on hydration.
  const [now, setNow] = useState<Date | null>(null);
  const [defaultBfLong, setDefaultBfLong] = useState<string>("");

  useEffect(() => {
    const bf = nextBlackFriday();
    setTargetDate(toDateValue(bf));
    setDefaultBfLong(fmtLong(bf));
    setNow(today());
  }, []);

  const plan = useMemo<Milestone[] | null>(() => {
    if (!targetDate) return null;
    const bf = new Date(`${targetDate}T00:00:00`);
    if (Number.isNaN(bf.getTime())) return null;
    return buildPlan(bf, leadDays);
  }, [targetDate, leadDays]);

  // Fire one analytics event ~800ms after the user stops adjusting.
  const posthog = usePostHog();
  useEffect(() => {
    if (!plan) return;
    const timer = setTimeout(() => {
      posthog?.capture("bf_tool_calculated", {
        tool: "backwards-planner",
        leadDays,
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [posthog, plan, leadDays]);

  function downloadIcs() {
    if (!plan) return;
    const blob = new Blob([buildIcs(plan)], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "black-friday-plan.ics";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // iOS Safari reads the blob after the save dialog; revoking immediately
    // causes a silent no-op (WEB-669).
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  return (
    <div className="border-grey-100 bg-surface relative rounded-2xl border p-5 sm:p-7">
      <div className="grid gap-6 lg:grid-cols-[1fr_30rem] lg:gap-10">
        {/* Inputs */}
        <div>
          <h2 className="text-heading-16 mb-6 font-semibold text-[#0D0D0D]">
            {STRINGS.toolTitle}
          </h2>

          <div className="mb-7">
            <label
              htmlFor="bf-target-date"
              className="text-body-sm mb-2 block font-semibold text-[#0D0D0D]"
            >
              {STRINGS.dateLabel}
            </label>
            <input
              type="date"
              id="bf-target-date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="text-body-16 border-grey-100 focus-visible:outline-pulse-700 h-14 w-full max-w-xs rounded-lg border bg-white px-4 text-[#0D0D0D] focus-visible:outline-2 focus-visible:-outline-offset-1"
            />
            {defaultBfLong && (
              <p className="text-body-14 text-grey-400 mt-2">
                Defaults to Black Friday, {defaultBfLong}. Launching your sale
                in early November? Set your launch date instead - every deadline
                moves up with it.
              </p>
            )}
          </div>

          <div>
            <span className="text-body-sm mb-2 block font-semibold text-[#0D0D0D]">
              {STRINGS.leadLabel}
            </span>
            <div
              role="group"
              aria-label={STRINGS.leadLabel}
              className="flex flex-wrap gap-2"
            >
              {LEAD_OPTIONS.map((opt) => {
                const active = opt.days === leadDays;
                return (
                  <button
                    key={opt.days}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setLeadDays(opt.days)}
                    className={`text-body-14 focus-visible:outline-pulse-700 cursor-pointer rounded-full border px-4 py-2 font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 ${
                      active
                        ? "border-pulse-700 bg-pulse-700 text-white"
                        : "border-grey-100 hover:border-grey-400 bg-white text-[#0D0D0D]"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="text-body-14 text-grey-400 mt-2">
              {STRINGS.leadHint}
            </p>
          </div>
        </div>

        {/* Result */}
        <div aria-live="polite" className="bg-pulse-50 rounded-xl p-5 sm:p-6">
          {plan && now ? (
            <Result plan={plan} now={now} onDownload={downloadIcs} />
          ) : (
            <p className="text-heading-24 lg:text-heading-32 leading-tight text-[#0D0D0D]">
              {STRINGS.resultPrompt}
            </p>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <p className="text-body-14 text-text-label max-w-xl italic">
          {STRINGS.disclaimer}
        </p>
        <WayflyerLogo
          aria-hidden="true"
          className="h-5 w-auto shrink-0 md:h-6 md:w-auto"
        />
      </div>
    </div>
  );
}

function Result({
  plan,
  now,
  onDownload,
}: {
  plan: Milestone[];
  now: Date;
  onDownload: () => void;
}) {
  const nowMs = now.getTime();
  const hero = plan.find((m) => m.hero);
  const balance = plan.find((m) => m.key === "balance");
  const bf = plan.find((m) => m.marker);
  const nextUp = plan.find((m) => !m.marker && m.date.getTime() >= nowMs);

  return (
    <>
      <p className="text-heading-24 lg:text-heading-32 mb-5 leading-tight text-[#0D0D0D]">
        {hero && <Headline hero={hero} nowMs={nowMs} />}
      </p>

      <div className="border-pulse-100 border-t">
        {plan.map((m) => (
          <MilestoneRow
            key={m.name}
            milestone={m}
            nowMs={nowMs}
            isNext={m === nextUp}
          />
        ))}
      </div>

      {hero && balance && bf && (
        <CashNote hero={hero} balance={balance} bf={bf} nowMs={nowMs} />
      )}

      <button
        type="button"
        onClick={onDownload}
        className="text-body-14 border-pulse-700 text-pulse-700 hover:bg-pulse-700 focus-visible:outline-pulse-700 mt-5 cursor-pointer rounded-full border px-4 py-2 font-semibold transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        {STRINGS.icsButton}
      </button>
    </>
  );
}

function Headline({ hero, nowMs }: { hero: Milestone; nowMs: number }) {
  if (hero.date.getTime() >= nowMs) {
    const weeksAway = weeksBetween(new Date(nowMs), hero.date);
    const timing =
      weeksAway <= 0
        ? "That's this week."
        : weeksAway === 1
          ? "That's one week away."
          : `That's ${weeksAway} weeks away.`;
    return (
      <>
        Order your stock by{" "}
        <span className="text-pulse-700">{fmtLong(hero.date)}</span>. {timing}
      </>
    );
  }
  return (
    <>
      Your stock order deadline was{" "}
      <span className="text-pulse-700">{fmtLong(hero.date)}</span>. Ask your
      supplier about faster options, or plan the sale around stock you can get
      in time.
    </>
  );
}

function MilestoneRow({
  milestone: m,
  nowMs,
  isNext,
}: {
  milestone: Milestone;
  nowMs: number;
  isNext: boolean;
}) {
  const passed = m.date.getTime() < nowMs;
  const highlight = (m.marker || (m.hero && !passed)) ?? false;

  const nameColor = highlight
    ? "text-pulse-700"
    : passed
      ? "text-[#9ca3af]"
      : "text-[#0D0D0D]";
  const dateColor = highlight
    ? "text-pulse-700"
    : passed
      ? "text-[#9ca3af] line-through"
      : "text-[#0D0D0D]";

  return (
    <div
      className={`flex items-start justify-between gap-4 py-3 ${
        m.marker
          ? "border-pulse-200 mt-1 border-t-2"
          : "border-pulse-100 border-b last:border-b-0"
      }`}
    >
      <div className="min-w-0">
        <p
          className={`text-body-14 flex flex-wrap items-center gap-2 font-semibold ${nameColor}`}
        >
          {m.name}
          {!m.marker && passed && (
            <span className="rounded-full bg-[#fdf0e3] px-2 py-0.5 text-[10.5px] font-bold tracking-wide text-[#b45309] uppercase">
              Passed
            </span>
          )}
          {!m.marker && !passed && isNext && (
            <span className="bg-pulse-100 text-pulse-700 rounded-full px-2 py-0.5 text-[10.5px] font-bold tracking-wide uppercase">
              Next up
            </span>
          )}
        </p>
        <p
          className={`mt-0.5 text-[12.5px] leading-snug ${passed ? "text-[#9ca3af]" : "text-grey-400"}`}
        >
          {m.desc}
        </p>
      </div>
      <span
        className={`text-body-14 shrink-0 pt-px font-bold whitespace-nowrap ${dateColor}`}
      >
        {fmt(m.date)}
      </span>
    </div>
  );
}

function CashNote({
  hero,
  balance,
  bf,
  nowMs,
}: {
  hero: Milestone;
  balance: Milestone;
  bf: Milestone;
  nowMs: number;
}) {
  if (hero.date.getTime() >= nowMs) {
    const depositWeeks = weeksBetween(hero.date, bf.date);
    const balanceWeeks = weeksBetween(balance.date, bf.date);
    return (
      <p className="text-body-14 border-pulse-100 mt-4 border-t pt-4 leading-relaxed text-[#3f4453]">
        <strong>Cash heads-up.</strong> Your deposit leaves your account around{" "}
        {depositWeeks} weeks before Black Friday revenue lands, and the balance
        around {balanceWeeks} {balanceWeeks === 1 ? "week" : "weeks"} before.
        Make sure your cash flow can carry both payments through the gap.
      </p>
    );
  }
  return (
    <p className="text-body-14 border-pulse-100 mt-4 border-t pt-4 leading-relaxed text-[#3f4453]">
      <strong>Cash heads-up.</strong> Shorter lead times mean paying your
      supplier closer to the sale, which eases the gap between the deposit and
      balance leaving your account and Black Friday revenue landing.
    </p>
  );
}
